import React, { useState, useContext, useEffect, useRef } from 'react';
import { Modal, RadioButton } from '@everlywell/leaves';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import { ProductInfoContext } from '../../../../templates/pdp';
import {
  SubscriptionOptionType,
  SUBSCRIBE,
} from '../../../../containers/PDP/HeroContainer';
import StripePayment from 'components/Stripe/StripePayment';

import MemberRedemptionModal from '../../../MemberRedemptionModal';
import DisclaimerModal from '../DisclaimerModal';

import { currentOrderGrandTotalInCents, orderMembershipKit } from 'utils/cartHelpers';
import { generateKey } from 'utils/helpers';
import { Flags } from 'utils/constants/featureFlags';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { Flagged } from 'components/Flagged';
import { CartContext, UserContext } from 'components/Layout';

import * as S from './styles';
import CreditRedemptionModal from '../../../MembershipPDP/CreditRedemptionModal';
const { APP_ROOT } = require('utils/constants/urls');

const stripePromise = loadStripe(stripePublicKey);

const COVID_SLUG = 'covid-19-test';

export type PurchaseTypeRadioButton = {
  label: string;
  name: string;
};

export type PurchaseControlsProps = {
  onPurchaseTypeChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onSubscriptionOptionChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onAddToCartClick: (e: React.FormEvent<HTMLButtonElement>) => void;
  onQuantityPickerChange: (e: React.FormEvent<HTMLInputElement>) => void;
  purchaseTypeRadioButtons: PurchaseTypeRadioButton[];
  selectedPurchaseType: string;
  selectedSubscriptionOption: SubscriptionOptionType;
  subscriptionOptions: {
    label: string;
    name: string;
    savingsStatement: string;
  }[];
  activeCredits?: number;
  openMemberModal: boolean;
  setOpenMemberModal: (arg0: any) => void;
  membershipVariantId: number;
  isStickyAppearance?: boolean;
  hideSubscription?: boolean;
  onFrequencyTriggerClick?: Function;
  onExpressPayClick: Function;
  stripeTotal: string;
  ctaSubheadline: string;
  quantity: number;
};

export const PurchaseControls = ({
  onPurchaseTypeChange,
  onSubscriptionOptionChange,
  purchaseTypeRadioButtons,
  selectedPurchaseType,
  selectedSubscriptionOption,
  subscriptionOptions,
  onQuantityPickerChange,
  onAddToCartClick,
  activeCredits,
  openMemberModal,
  setOpenMemberModal,
  membershipVariantId,
  isStickyAppearance = false,
  hideSubscription,
  onFrequencyTriggerClick,
  onExpressPayClick,
  stripeTotal,
  ctaSubheadline,
  quantity,
}: PurchaseControlsProps): JSX.Element => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [openDisclaimerModal, setOpenDisclaimerModal] = useState<boolean>(
    false,
  );
  const [hasLoaded, setHasLoaded] = useState(false);
  const { setTOS } = useContext(CartContext);
  const { slug, sku } = useContext(ProductInfoContext);
  const { userId } = useContext(UserContext);
  const orderButtonRef = useRef(null);
  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    onSubscriptionOptionChange(e);
  };

  const handleModalAddToCartClick = (e: React.FormEvent<HTMLButtonElement>) => {
    onAddToCartClick(e);
    setOpenModal(false);
  };

  const handleFrequencyTriggerClick = () => {
    onFrequencyTriggerClick && onFrequencyTriggerClick();
    setOpenModal(true);
  };

  const handleAddToCartClick = (e: React.FormEvent<HTMLButtonElement>) => {
    if (isCovid) {
      setOpenDisclaimerModal(true);
      // the Disclaimer Modal opens and will have the continue button to redirect to checkout
    } else {
      onAddToCartClick(e);
      setIsDisabled(true);
      setTimeout(() => {
        setIsDisabled(false);
      }, 600);
    }
  };

  const handleOrderClick = (
  ) => {
    if (userId)
    orderMembershipKit(
      orderButtonRef,
      membershipVariantId,
      userId,
      (res: any) => {
        const { id: orderId } = res;
        window.location.href = `${APP_ROOT}/confirmation?orderId=${orderId}&?membershipCreditRedemption=true`;
      },
    );
  };

  const isCovid = slug === COVID_SLUG;
  const ctaText = isCovid ? 'Order' : 'Add to Cart';
  const flags = useFlags();
  const covidQuestionaireFlag = flags[Flags.CovidQtyAndScreener];

  useEffect(() => setHasLoaded(true), [stripePromise]);

  return (
    <>
      {!isStickyAppearance && !hideSubscription ? (
        <S.Fieldset>
          <S.Legend
            data-testid="purchase-control-legend"
            hideSubscription={hideSubscription}
          >
            one time purchase or subscribe
          </S.Legend>
          <S.ButtonsWrapper hideSubscription={hideSubscription}>
            {selectedSubscriptionOption
              ? purchaseTypeRadioButtons.map((button) => (
                  <S.RadioWrapper key={generateKey()}>
                    <RadioButton
                      id={button.name}
                      name={button.name}
                      label={
                        button.name === SUBSCRIBE
                          ? `${selectedSubscriptionOption.name} Subscription (Save ${selectedSubscriptionOption.percentDiscount}%)`
                          : button.label
                      }
                      onChange={onPurchaseTypeChange}
                      checked={selectedPurchaseType === button.name}
                    />
                    {button.name === SUBSCRIBE ? (
                      <>
                        <S.FrequencyTrigger
                          onClick={handleFrequencyTriggerClick}
                          data-testid="frequencyModalTrigger"
                        >
                          Change Frequency
                        </S.FrequencyTrigger>
                        {openModal && (
                          <Modal open={openModal} openHandler={setOpenModal}>
                            <S.ModalContent>
                              <S.SubscriptionModalHeader>
                                Select Subscription Frequency
                              </S.SubscriptionModalHeader>
                              <S.FieldsetWrapper>
                                <S.SubscriptionFieldset>
                                  {subscriptionOptions.map((option) => (
                                    <S.SubscriptionOption key={generateKey()}>
                                      <RadioButton
                                        id={option.name}
                                        data-testid="subscriptionOption"
                                        name={option.name}
                                        label={option.label}
                                        onChange={handleChange}
                                        checked={
                                          selectedSubscriptionOption.name ===
                                          option.name
                                        }
                                      />
                                      <S.Savings>
                                        {option.savingsStatement}
                                      </S.Savings>
                                    </S.SubscriptionOption>
                                  ))}
                                </S.SubscriptionFieldset>
                              </S.FieldsetWrapper>

                              <S.ModalCTAWrapper>
                                <S.Gradient />
                                <S.CTA
                                  appearance="hero"
                                  onClick={handleModalAddToCartClick}
                                  data-testid="addToCartButton"
                                  data-name={ANALYTICS.LABELS.ADD_TO_CART_MODAL}
                                  isDisabled={isDisabled}
                                >
                                  Add to Cart
                                </S.CTA>
                                <S.CTASubheadline>
                                  {ctaSubheadline}
                                </S.CTASubheadline>
                              </S.ModalCTAWrapper>
                            </S.ModalContent>
                          </Modal>
                        )}
                      </>
                    ) : null}
                  </S.RadioWrapper>
                ))
              : null}
          </S.ButtonsWrapper>
        </S.Fieldset>
      ) : null}
      {isStickyAppearance ? (
        <S.StickyPurchaseWrapper>
          {isCovid ? (
            <Flagged flag={covidQuestionaireFlag}>
              <S.StickyQuantityPicker
                onQuantityPickerChange={onQuantityPickerChange}
                numOfOptions={99}
                selectedOption={quantity}
              />
            </Flagged>
          ) : null}
          <S.StickyMobileAddToCart
            appearance="hero"
            onClick={handleAddToCartClick}
            data-testid="stickyMobileAddToCartButton"
            isDisabled={isDisabled}
          >
            {ctaText}
          </S.StickyMobileAddToCart>
        </S.StickyPurchaseWrapper>
      ) : null}

      <S.DesktopCTAWrapper isStickyAppearance={isStickyAppearance}>
        <S.ButtonAndQtyPicker>
          {activeCredits && selectedPurchaseType !== SUBSCRIBE ? (
            <>
              <CreditRedemptionModal
                open={openMemberModal}
                confirmOrder={handleOrderClick}
                openHandler={setOpenMemberModal}
              />
              <S.CTA
                appearance="hero"
                onClick={() => setOpenMemberModal(true)}
                data-testid="orderMemberTestButton"
                ref={orderButtonRef}
              >
                Order
              </S.CTA>
            </>
          ) : (
            <>
              {isCovid ? (
                <Flagged flag={covidQuestionaireFlag}>
                  <S.MobileQuantityPicker
                    onQuantityPickerChange={onQuantityPickerChange}
                    numOfOptions={99}
                    selectedOption={quantity}
                  />
                </Flagged>
              ) : (
                <S.QuantityPicker
                  onQuantityPickerChange={onQuantityPickerChange}
                  selectedOption={quantity}
                />
              )}
              <S.CTA
                appearance="hero"
                onClick={handleAddToCartClick}
                data-testid="addToCartButton"
                isDisabled={isDisabled}
              >
                {ctaText}
              </S.CTA>
              {hasLoaded && !isCovid ? (
                <Elements stripe={stripePromise}>
                  <StripePayment
                    cartTotal={currentOrderGrandTotalInCents(+stripeTotal)}
                    setTOS={setTOS}
                    onPaymentConfirm={onExpressPayClick}
                    cartNotEmpty={true}
                    optionType="default"
                    optionTheme="light-outline"
                    label={ANALYTICS.LABELS.EXPRESS_PAY_PDP}
                  />
                </Elements>
              ) : null}
            </>
          )}
        </S.ButtonAndQtyPicker>
        {!isStickyAppearance ? (
          <S.CTASubheadline>{ctaSubheadline}</S.CTASubheadline>
        ) : null}
      </S.DesktopCTAWrapper>
      <DisclaimerModal
        open={openDisclaimerModal}
        openHandler={setOpenDisclaimerModal}
        productSku={sku}
        selectedQuantity={quantity}
      />
    </>
  );
};

export default PurchaseControls;
